import { useEffect, useRef, useCallback, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import {
  MapPin, CaretRight, MagnifyingGlass, ArrowsClockwise,
  WifiSlash, Pulse, Thermometer, Drop, Lightbulb, Warning,
  Cpu, BatteryFull, MapPinArea, Plus, X, Crosshair, CheckCircle, Target,
} from "@phosphor-icons/react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMP_MIN = 32;
const TEMP_MAX = 40;
const HUM_MIN = 20;
const HUM_MAX = 80;
const OFFLINE_HOURS = 24;

// Classify a device into a derived severity bucket
function classifyDevice(d) {
  const flags = {
    offline: false,
    light_breach: false,
    temp_excursion: false,
    humidity_excursion: false,
    severity: "normal", // critical | warning | normal | offline
  };

  // Offline check
  if (!d.last_reading) {
    flags.offline = true;
  } else {
    const last = new Date(d.last_reading);
    if (!isNaN(last.getTime())) {
      const hoursAgo = (Date.now() - last.getTime()) / 3_600_000;
      if (hoursAgo > OFFLINE_HOURS) flags.offline = true;
    }
  }

  // Light/seal breach
  if (d.light && d.light.toLowerCase() !== "dark") flags.light_breach = true;

  // Temp / humidity excursions
  let tempDeviation = 0;
  if (d.temperature != null) {
    if (d.temperature < TEMP_MIN) tempDeviation = TEMP_MIN - d.temperature;
    else if (d.temperature > TEMP_MAX) tempDeviation = d.temperature - TEMP_MAX;
    if (tempDeviation > 0) flags.temp_excursion = true;
  }
  if (d.humidity != null && (d.humidity < HUM_MIN || d.humidity > HUM_MAX)) {
    flags.humidity_excursion = true;
  }

  // Severity
  if (flags.offline) {
    flags.severity = "offline";
  } else if (
    flags.light_breach ||
    tempDeviation > 5 ||
    (d.battery_level != null && d.battery_level < 10)
  ) {
    flags.severity = "critical";
  } else if (
    d.status === "alert" ||
    flags.temp_excursion ||
    flags.humidity_excursion ||
    (d.battery_level != null && d.battery_level < 20)
  ) {
    flags.severity = "warning";
  }
  return flags;
}

const SEV_COLORS = {
  critical: "#EF4444",
  warning: "#F59E0B",
  normal: "#00DCC2",
  offline: "#71717A",
};

function fmtRelativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.substring(0, 16);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "critical", label: "Critical" },
  { key: "warning", label: "Warning" },
  { key: "normal", label: "Normal" },
  { key: "offline", label: "Offline" },
  { key: "light_breach", label: "Light/Seal" },
  { key: "temp_excursion", label: "Temp" },
  { key: "humidity_excursion", label: "Humidity" },
];

export default function FleetMapView({ onSelectDevice, onSelectShipment }) {
  const { user } = useAuth();
  const canEditGeofences = ["super_admin", "admin", "org_admin"].includes(user?.role);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const clusterRef = useRef(null);
  const markerByDeviceRef = useRef({});
  const hasAutoFitRef = useRef(false);     // auto-fit only ONCE so subsequent fly/setView aren't stolen back
  const tileLayerRef = useRef(null);
  const [basemap, setBasemap] = useState("dark");   // "dark" | "satellite"
  const geofenceLayerRef = useRef(null);
  const draftMarkerRef = useRef(null);
  const draftCircleRef = useRef(null);
  const previewCircleRef = useRef(null);
  const editLayerRef = useRef(null);

  const [devices, setDevices] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [showGeofences, setShowGeofences] = useState(true);
  const [hiddenTypes, setHiddenTypes] = useState(() => new Set());  // unchecked types are hidden
  const [geofencePanelOpen, setGeofencePanelOpen] = useState(false);
  const [geofenceSearch, setGeofenceSearch] = useState("");

  // Edit flow: null | {id, name, type, latitude, longitude, radius_m, notes, _orig:{...}}
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // Draw flow: idle | center | radius
  const [drawMode, setDrawMode] = useState("idle");
  const [draft, setDraft] = useState(null);              // {lat, lng, radius_m}
  const [showDraftForm, setShowDraftForm] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftType, setDraftType] = useState("Warehouse");
  const [draftNotes, setDraftNotes] = useState("");

  // Address search (Nominatim — free, no key)
  const [addressQuery, setAddressQuery] = useState("");
  const [addressResults, setAddressResults] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressFocused, setAddressFocused] = useState(false);
  const skipNextAddressSearchRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [popup, setPopup] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchDevices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/fleet/devices?limit=5000`);
      setDevices(res.data.devices || res.data || []);
      setLastRefresh(new Date());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchGeofences = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/geofences`);
      setGeofences(res.data.items || []);
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => { fetchDevices(); fetchGeofences(); }, [fetchDevices, fetchGeofences]);

  // Live updates: refetch device list on event/alert (debounced)
  const refetchTimer = useRef(null);
  const handleLive = useCallback((msg) => {
    if (!["event.ingested", "alert.created", "device.status_changed"].includes(msg.type)) return;
    if (refetchTimer.current) return;
    refetchTimer.current = setTimeout(() => {
      refetchTimer.current = null;
      fetchDevices();
      fetchGeofences();
    }, 800);
  }, [fetchDevices, fetchGeofences]);
  const { status: liveStatus } = useLiveUpdates(handleLive);

  // Polling fallback when WS not connected
  useEffect(() => {
    if (liveStatus === "connected") return;
    const id = setInterval(fetchDevices, 30_000);
    return () => clearInterval(id);
  }, [liveStatus, fetchDevices]);

  // Enrich devices with classification
  const classifiedDevices = useMemo(() => {
    return devices.map((d) => ({ ...d, _flags: classifyDevice(d) }));
  }, [devices]);

  // Counts per severity (full fleet, not filtered)
  const counts = useMemo(() => {
    const c = { critical: 0, warning: 0, normal: 0, offline: 0, total: classifiedDevices.length };
    classifiedDevices.forEach((d) => { c[d._flags.severity]++; });
    return c;
  }, [classifiedDevices]);

  const filteredDevices = useMemo(() => {
    return classifiedDevices.filter((d) => {
      if (search) {
        const q = search.toLowerCase();
        const matches = [
          d.device_name,
          d.device_id,
          d.address,
          d.shipment_name,
          d.customer,
        ].some((v) => v && String(v).toLowerCase().includes(q));
        if (!matches) return false;
      }
      const f = d._flags;
      switch (filter) {
        case "all": return true;
        case "critical": return f.severity === "critical";
        case "warning": return f.severity === "warning";
        case "normal": return f.severity === "normal";
        case "offline": return f.severity === "offline";
        case "light_breach": return f.light_breach;
        case "temp_excursion": return f.temp_excursion;
        case "humidity_excursion": return f.humidity_excursion;
        default: return true;
      }
    });
  }, [classifiedDevices, search, filter]);

  const withLocation = filteredDevices.filter((d) => d.latitude && d.longitude);

  // Build map once
  useEffect(() => {
    if (!mapRef.current || loading) return;
    if (mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      zoomControl: true,
      attributionControl: false,
    }).setView([42.5, -76.0], 5);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "&copy; OpenStreetMap contributors" }).addTo(map);

    // Stored separately so we can swap dark<->satellite without rebuilding the map.
    tileLayerRef.current = map._layers
      ? Object.values(map._layers).find((l) => l instanceof L.TileLayer) || null
      : null;

    const cluster = L.markerClusterGroup({
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      iconCreateFunction: (clust) => {
        const count = clust.getChildCount();
        const markers = clust.getAllChildMarkers();
        const sevs = markers.map((m) => m.options.severity);
        const color = sevs.includes("critical") ? SEV_COLORS.critical :
                      sevs.includes("warning") ? SEV_COLORS.warning :
                      sevs.includes("offline") ? SEV_COLORS.offline : SEV_COLORS.normal;
        return L.divIcon({
          html: `<div style="background:${color};color:#fff;width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;font-family:monospace;box-shadow:0 2px 8px ${color}66;border:2px solid rgba(255,255,255,0.15);">${count}</div>`,
          className: "",
          iconSize: [36, 36],
        });
      },
    });
    map.addLayer(cluster);

    clusterRef.current = cluster;
    mapInstanceRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      clusterRef.current = null;
      markerByDeviceRef.current = {};
      hasAutoFitRef.current = false;
    };
  }, [loading]);

  // Swap base tile layer when user toggles Dark / Satellite.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (tileLayerRef.current) {
      map.removeLayer(tileLayerRef.current);
    }
    const url = basemap === "satellite"
      ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
      : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
    const layer = L.tileLayer(url, {
      maxZoom: 19,
      // Keep the keyless OSM default visually dark without relying on CARTO.
      className: basemap === "dark" ? "dark-osm-tiles" : "",
      attribution: basemap === "satellite"
        ? "Imagery &copy; Esri, Maxar, Earthstar Geographics"
        : "&copy; OpenStreetMap contributors",
    });
    layer.addTo(map);
    // Send tiles to the back so device markers / geofences stay on top.
    if (layer.bringToBack) layer.bringToBack();
    tileLayerRef.current = layer;
  }, [basemap]);

  // Update markers when filtered list changes
  useEffect(() => {
    const map = mapInstanceRef.current;
    const cluster = clusterRef.current;
    if (!map || !cluster) return;

    cluster.clearLayers();
    markerByDeviceRef.current = {};

    withLocation.forEach((device) => {
      const sev = device._flags.severity;
      const color = SEV_COLORS[sev];
      const marker = L.circleMarker([device.latitude, device.longitude], {
        radius: 8,
        fillColor: color,
        color: color,
        weight: 2,
        fillOpacity: 0.9,
        severity: sev,
      });
      marker.on("click", () => {
        setPopup(device);
        setSelectedDevice(device.device_name);
      });
      cluster.addLayer(marker);
      markerByDeviceRef.current[device.device_name] = marker;
    });

    // Auto-fit ONCE on the very first render so the user lands on their fleet.
    // After that, leave the camera under user control — programmatic
    // navigation (address fly-to, geofence panel, draw-mode) must not be
    // overridden by the next device-update tick.
    if (!hasAutoFitRef.current) {
      if (withLocation.length > 1) {
        try {
          map.fitBounds(withLocation.map((d) => [d.latitude, d.longitude]), {
            padding: [50, 50], maxZoom: 14,
          });
        } catch (e) { /* noop */ }
      } else if (withLocation.length === 1) {
        map.setView([withLocation[0].latitude, withLocation[0].longitude], 11);
      }
      if (withLocation.length > 0) hasAutoFitRef.current = true;
    }
    map.invalidateSize();
  }, [withLocation]);

  // Render geofences as colored circles on a dedicated layer
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (!geofenceLayerRef.current) {
      geofenceLayerRef.current = L.layerGroup().addTo(map);
    }
    const layer = geofenceLayerRef.current;
    layer.clearLayers();
    if (!showGeofences) return;

    const TYPE_COLOR = {
      "Warehouse": "#2dd4bf", "Distribution Center": "#06b6d4",
      "Airport Cargo Facility": "#a78bfa", "Port": "#60a5fa",
      "Customer Facility": "#34d399", "Hospital": "#f87171",
      "Pharmacy": "#fb923c", "Origin": "#facc15",
      "Destination": "#84cc16", "Custom": "#9ca3af",
    };
    geofences.forEach((g) => {
      if (hiddenTypes.has(g.type)) return;
      if (editing && editing.id === g.id) return;  // skip the one being edited (edit overlay owns it)
      const color = TYPE_COLOR[g.type] || "#9ca3af";
      // Circle (fence)
      const circle = L.circle([g.latitude, g.longitude], {
        radius: g.radius_m, color, weight: 2,
        fillColor: color, fillOpacity: 0.10,
        dashArray: "4 3",
      });
      // Center marker w/ tiny dot
      const center = L.marker([g.latitude, g.longitude], {
        icon: L.divIcon({
          className: "geofence-center-pin",
          html: `<div style="background:${color};width:10px;height:10px;border-radius:50%;border:2px solid #0a0a0a;box-shadow:0 0 0 2px ${color}66"></div>`,
          iconSize: [10, 10], iconAnchor: [5, 5],
        }),
      });
      const editBtn = canEditGeofences
        ? `<button data-gf-edit="${g.id}" style="margin-top:8px;background:#2dd4bf;color:#0a0a0a;font-weight:600;font-size:10px;text-transform:uppercase;letter-spacing:.05em;padding:4px 10px;border:0;border-radius:2px;cursor:pointer;width:100%;">Edit on map</button>`
        : '';
      // Last edited breadcrumb — small subtle gray line.
      let lastLine = '';
      if (g.last_audit && g.last_audit.timestamp) {
        const t = new Date(g.last_audit.timestamp);
        const mins = Math.max(0, Math.round((Date.now() - t.getTime()) / 60000));
        const ago = mins < 1 ? "just now"
          : mins < 60 ? `${mins}m ago`
          : mins < 1440 ? `${Math.round(mins/60)}h ago`
          : `${Math.round(mins/1440)}d ago`;
        const who = g.last_audit.user_email || "someone";
        lastLine = `<div style="font-size:10px;color:#71717a;margin-top:6px;border-top:1px solid #27272a;padding-top:6px;">Last edited ${ago} by <span style="color:#a1a1aa;">${who}</span></div>`;
      } else if (g.created_by) {
        lastLine = `<div style="font-size:10px;color:#71717a;margin-top:6px;border-top:1px solid #27272a;padding-top:6px;">Created by <span style="color:#a1a1aa;">${g.created_by}</span></div>`;
      } else {
        lastLine = `<div style="font-size:10px;color:#52525b;margin-top:6px;border-top:1px solid #27272a;padding-top:6px;font-style:italic;">No edit history yet.</div>`;
      }
      const popup = `<div style="font-family:'SF Pro Text',ui-sans-serif;color:#e4e4e7;min-width:200px;">
        <div style="font-weight:600;font-size:12px;color:${color};">${g.name}</div>
        <div style="font-size:10px;color:#71717a;text-transform:uppercase;letter-spacing:.05em;margin-top:2px;">${g.type}</div>
        <div style="font-size:10px;color:#a1a1aa;margin-top:6px;font-family:ui-monospace;">
          ${(g.radius_m >= 1000 ? (g.radius_m/1000).toFixed(g.radius_m % 1000 === 0 ? 0 : 2) + ' km' : Math.round(g.radius_m) + ' m')}
          · ${g.latitude.toFixed(4)}, ${g.longitude.toFixed(4)}
        </div>
        ${g.notes ? `<div style="font-size:10px;color:#a1a1aa;margin-top:4px;font-style:italic;">${g.notes}</div>` : ''}
        ${lastLine}
        ${editBtn}
      </div>`;
      circle.bindPopup(popup);
      center.bindPopup(popup);
      layer.addLayer(circle);
      layer.addLayer(center);
    });
    map.invalidateSize();
  }, [geofences, showGeofences, hiddenTypes, editing, canEditGeofences]);

  // Delegate clicks on the "Edit on map" button rendered inside popups
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const root = map.getContainer();
    const onClick = (e) => {
      const t = e.target.closest("[data-gf-edit]");
      if (!t) return;
      const id = t.getAttribute("data-gf-edit");
      const g = geofences.find((x) => x.id === id);
      if (!g) return;
      if (!canEditGeofences) {
        toast.error("You don't have permission to edit geofences");
        return;
      }
      map.closePopup();
      setEditing({ ...g, _orig: { ...g } });
    };
    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [geofences, canEditGeofences]);

  // ============================================================
  // EDIT MODE — drag center & edge handles to move/resize a fence
  // ============================================================
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (editLayerRef.current) {
      editLayerRef.current.remove();
      editLayerRef.current = null;
    }
    if (!editing) return;

    const color = "#2dd4bf";
    const layer = L.layerGroup().addTo(map);
    editLayerRef.current = layer;

    // Outer dashed editing circle (always reflects current radius)
    const circle = L.circle([editing.latitude, editing.longitude], {
      radius: editing.radius_m, color, weight: 2,
      fillColor: color, fillOpacity: 0.12, dashArray: "6 4",
    }).addTo(layer);

    // Center handle (draggable)
    const centerHandle = L.marker([editing.latitude, editing.longitude], {
      draggable: true,
      icon: L.divIcon({
        className: "gf-center-handle",
        html: `<div style="background:${color};width:16px;height:16px;border-radius:50%;border:2px solid #0a0a0a;box-shadow:0 0 0 3px ${color}99,0 0 16px ${color}55;cursor:move;"></div>`,
        iconSize: [16, 16], iconAnchor: [8, 8],
      }),
    }).addTo(layer);

    // Edge handle — placed due east of center at distance = radius (projected)
    const edgePos = () => {
      const c = circle.getLatLng();
      const r = circle.getRadius();
      // Approximate longitude offset for `r` meters at this latitude.
      const dLng = (r / (111_320 * Math.cos((c.lat * Math.PI) / 180)));
      return L.latLng(c.lat, c.lng + dLng);
    };
    const edgeHandle = L.marker(edgePos(), {
      draggable: true,
      icon: L.divIcon({
        className: "gf-edge-handle",
        html: `<div style="background:#0a0a0a;width:14px;height:14px;border-radius:50%;border:2px solid ${color};box-shadow:0 0 0 2px ${color}66;cursor:ew-resize;"></div>`,
        iconSize: [14, 14], iconAnchor: [7, 7],
      }),
    }).addTo(layer);

    const sync = () => {
      edgeHandle.setLatLng(edgePos());
    };

    centerHandle.on("drag", (e) => {
      const ll = e.target.getLatLng();
      circle.setLatLng(ll);
      sync();
    });
    centerHandle.on("dragend", (e) => {
      const ll = e.target.getLatLng();
      setEditing((prev) => prev ? { ...prev, latitude: ll.lat, longitude: ll.lng } : prev);
    });

    edgeHandle.on("drag", (e) => {
      const c = circle.getLatLng();
      const r = Math.max(25, map.distance(c, e.target.getLatLng()));
      circle.setRadius(r);
    });
    edgeHandle.on("dragend", () => {
      const r = Math.max(25, Math.min(200_000, Math.round(circle.getRadius())));
      circle.setRadius(r);
      sync();
      setEditing((prev) => prev ? { ...prev, radius_m: r } : prev);
    });

    return () => {
      try { layer.remove(); } catch {}
      editLayerRef.current = null;
    };
  }, [editing?.id, editing?._orig?.id]);  // build once per fence; drag handlers manage live state

  // Keep the edit circle/handles in sync if user edits numeric fields too (or saves/cancels)
  useEffect(() => {
    if (!editLayerRef.current || !editing) return;
    const layers = editLayerRef.current.getLayers();
    const circle = layers.find((l) => l instanceof L.Circle);
    if (!circle) return;
    circle.setLatLng([editing.latitude, editing.longitude]);
    circle.setRadius(editing.radius_m);
  }, [editing?.latitude, editing?.longitude, editing?.radius_m]);

  const cancelEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editing) return;
    if (!editing.name?.trim()) { toast.error("Name is required"); return; }
    if (!editing.radius_m || editing.radius_m < 25) {
      toast.error("Radius must be at least 25 m"); return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/geofences/${editing.id}`, {
        name: editing.name.trim(),
        type: editing.type,
        latitude: parseFloat(editing.latitude),
        longitude: parseFloat(editing.longitude),
        radius_m: parseFloat(editing.radius_m),
        notes: editing.notes || null,
      });
      const o = editing._orig || {};
      const moved = (o.latitude !== editing.latitude) || (o.longitude !== editing.longitude);
      const resized = o.radius_m !== editing.radius_m;
      toast.success(`Saved · ${moved ? "moved" : ""}${moved && resized ? " + " : ""}${resized ? "resized" : ""}${!moved && !resized ? "no change" : ""}`);
      setEditing(null);
      fetchGeofences();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingEdit(false);
    }
  };

  // ============================================================
  // GEOFENCE DRAW FLOW (click-to-place center → click-to-set radius)
  // ============================================================
  // Map click + mousemove handlers for draw mode
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const onClick = (e) => {
      if (drawMode === "center") {
        setDraft({ lat: e.latlng.lat, lng: e.latlng.lng, radius_m: 0 });
        setDrawMode("radius");
      } else if (drawMode === "radius") {
        setDraft((d) => {
          if (!d) return d;
          const r = map.distance([d.lat, d.lng], [e.latlng.lat, e.latlng.lng]);
          return { ...d, radius_m: Math.max(50, Math.round(r)) };
        });
        setDrawMode("idle");
        setShowDraftForm(true);
      }
    };

    const onMove = (e) => {
      if (drawMode !== "radius" || !draft) return;
      const r = map.distance([draft.lat, draft.lng], [e.latlng.lat, e.latlng.lng]);
      if (!previewCircleRef.current) {
        previewCircleRef.current = L.circle([draft.lat, draft.lng], {
          radius: r, color: "#2dd4bf", weight: 2, fillColor: "#2dd4bf",
          fillOpacity: 0.10, dashArray: "5 4",
        }).addTo(map);
      } else {
        previewCircleRef.current.setRadius(r);
      }
    };

    map.on("click", onClick);
    map.on("mousemove", onMove);
    map.getContainer().style.cursor = drawMode !== "idle" ? "crosshair" : "";

    return () => {
      map.off("click", onClick);
      map.off("mousemove", onMove);
      map.getContainer().style.cursor = "";
    };
  }, [drawMode, draft]);

  // Render the in-flight draft (marker + final circle); clear preview
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (draftMarkerRef.current) { draftMarkerRef.current.remove(); draftMarkerRef.current = null; }
    if (draftCircleRef.current) { draftCircleRef.current.remove(); draftCircleRef.current = null; }
    if (drawMode !== "radius" && previewCircleRef.current) {
      previewCircleRef.current.remove();
      previewCircleRef.current = null;
    }
    if (!draft) return;
    draftMarkerRef.current = L.marker([draft.lat, draft.lng], {
      icon: L.divIcon({
        className: "geofence-draft-pin",
        html: `<div style="background:#2dd4bf;width:14px;height:14px;border-radius:50%;border:2px solid #0a0a0a;box-shadow:0 0 0 2px #2dd4bf99,0 0 14px #2dd4bf55"></div>`,
        iconSize: [14, 14], iconAnchor: [7, 7],
      }),
    }).addTo(map);
    if (draft.radius_m > 0) {
      draftCircleRef.current = L.circle([draft.lat, draft.lng], {
        radius: draft.radius_m, color: "#2dd4bf", weight: 2,
        fillColor: "#2dd4bf", fillOpacity: 0.15,
      }).addTo(map);
    }
  }, [draft, drawMode]);

  const startDraw = () => {
    setDraft(null);
    setDraftName(""); setDraftType("Warehouse"); setDraftNotes("");
    setShowDraftForm(false);
    setDrawMode("center");
    toast.info("Click the map to drop the center, then click again to set the radius");
  };

  const cancelDraw = () => {
    setDraft(null);
    setDraftName(""); setDraftType("Warehouse"); setDraftNotes("");
    setShowDraftForm(false);
    setDrawMode("idle");
  };

  const saveDraft = async () => {
    if (!draft) return;
    if (!draftName.trim()) { toast.error("Name is required"); return; }
    if (!draft.radius_m || draft.radius_m <= 0) { toast.error("Radius must be > 0"); return; }
    try {
      await axios.post(`${API}/geofences`, {
        name: draftName.trim(), type: draftType,
        latitude: draft.lat, longitude: draft.lng,
        radius_m: draft.radius_m, notes: draftNotes || null,
      });
      toast.success(`Geofence "${draftName}" created`);
      cancelDraw();
      fetchGeofences();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Save failed");
    }
  };

  // ============================================================
  // ADDRESS AUTOCOMPLETE (Nominatim — free, no key, 1 req/sec policy)
  // ============================================================
  useEffect(() => {
    if (skipNextAddressSearchRef.current) {
      skipNextAddressSearchRef.current = false;
      return;
    }
    if (!addressQuery || addressQuery.length < 3) {
      setAddressResults([]); return;
    }
    const t = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&q=${encodeURIComponent(addressQuery)}`;
        const res = await fetch(url, { headers: { "Accept": "application/json" } });
        const data = await res.json();
        setAddressResults(Array.isArray(data) ? data : []);
      } catch (e) {
        setAddressResults([]);
      } finally {
        setAddressLoading(false);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [addressQuery]);

  const flyToAddress = (r) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const lat = parseFloat(r.lat), lng = parseFloat(r.lon);
    if (isNaN(lat) || isNaN(lng)) return;
    // `setView` is more reliable than `flyTo` here — flyTo animations can be
    // cancelled by concurrent map updates (e.g. the cluster re-render after
    // closing the autocomplete dropdown), which silently drops the navigation.
    map.setView([lat, lng], 15, { animate: true, duration: 0.6 });
    map.invalidateSize();
    skipNextAddressSearchRef.current = true;
    setAddressResults([]);
    setAddressFocused(false);
    setAddressQuery(r.display_name);
    const input = document.querySelector('[data-testid="map-address-search"]');
    input?.blur();
  };

  // Compute lat/lng bounds for a circle WITHOUT attaching it to the map.
  // L.Circle.getBounds() requires the layer to be on a map (uses projection),
  // so we use the geodesic approximation: ~111.32 km per degree of latitude,
  // scaled by cos(lat) for longitude.
  const circleBounds = (lat, lng, radius_m) => {
    const dLat = radius_m / 111320;
    const cosLat = Math.cos((lat * Math.PI) / 180) || 1;
    const dLng = radius_m / (111320 * cosLat);
    return L.latLngBounds(
      [lat - dLat, lng - dLng],
      [lat + dLat, lng + dLng],
    );
  };

  // Fly to a single geofence with its circle bounds (so radius is fully visible).
  // Forces the layer on if the user hid it, otherwise the fence wouldn't be drawn.
  const flyToGeofence = (g) => {
    const map = mapInstanceRef.current;
    if (!map || !g) return;
    if (!showGeofences) setShowGeofences(true);
    if (hiddenTypes.has(g.type)) {
      setHiddenTypes((prev) => {
        const next = new Set(prev);
        next.delete(g.type);
        return next;
      });
    }
    const bounds = circleBounds(g.latitude, g.longitude, g.radius_m).pad(0.5);
    map.flyToBounds(bounds, { duration: 0.8, maxZoom: 16 });
  };

  // Fit map to ALL currently filtered geofences.
  const flyToAllGeofences = () => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const list = geofences.filter((g) => !hiddenTypes.has(g.type));
    if (list.length === 0) return;
    if (!showGeofences) setShowGeofences(true);
    if (list.length === 1) {
      flyToGeofence(list[0]); return;
    }
    // Merge bounds without attaching any layer.
    let merged = null;
    list.forEach((g) => {
      const b = circleBounds(g.latitude, g.longitude, g.radius_m);
      merged = merged ? merged.extend(b) : b;
    });
    if (merged) map.flyToBounds(merged.pad(0.15), { duration: 0.8, maxZoom: 14 });
  };

  // Center on a device when clicked in left panel
  const focusDevice = (device) => {
    setSelectedDevice(device.device_name);
    if (!device.latitude || !device.longitude) return;
    const map = mapInstanceRef.current;
    if (!map) return;
    map.flyTo([device.latitude, device.longitude], 14, { duration: 0.6 });
    setTimeout(() => {
      const m = markerByDeviceRef.current[device.device_name];
      if (m && clusterRef.current) {
        clusterRef.current.zoomToShowLayer(m, () => {
          setPopup(device);
        });
      } else {
        setPopup(device);
      }
    }, 300);
  };

  return (
    <div className="flex flex-col bg-[#1A1A1A]" data-testid="fleet-map-view"
      style={{ height: "calc(100vh - 52px)" }}>
      {/* Status Bar */}
      <div className="flex items-center justify-between gap-4 px-4 py-2 border-b border-zinc-800 bg-zinc-950/40">
        <div className="flex items-center gap-1.5 flex-wrap">
          <h2 className="text-sm font-semibold text-zinc-100 mr-3">Fleet Operations</h2>
          <StatusPill label="Critical" count={counts.critical} color="red" testid="status-critical" />
          <StatusPill label="Warning" count={counts.warning} color="amber" testid="status-warning" />
          <StatusPill label="Offline" count={counts.offline} color="zinc" testid="status-offline" />
          <StatusPill label="Normal" count={counts.normal} color="emerald" testid="status-normal" />
          <span className="text-zinc-700 mx-1">|</span>
          <span className="text-[10px] font-mono text-zinc-500" data-testid="status-shown">
            {filteredDevices.length} of {counts.total} shown
          </span>
          <span className="text-zinc-700 mx-1">|</span>
          <button onClick={() => setGeofencePanelOpen((v) => !v)}
            data-testid="toggle-geofences-btn"
            className={`text-[10px] font-mono px-2 py-1 rounded-sm border transition-colors flex items-center gap-1.5 ${
              geofencePanelOpen
                ? "bg-teal-500/10 text-teal-300 border-teal-500/30"
                : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800"
            }`}>
            <span className="w-1.5 h-1.5 rounded-full" style={{
              background: showGeofences && geofences.length > 0 ? "#2dd4bf" : "#52525b"
            }} />
            Geofences ({geofences.length})
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500" data-testid="last-refresh">
            <span className="relative flex h-1.5 w-1.5">
              {liveStatus === "connected" && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60" />
              )}
              <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                liveStatus === "connected" ? "bg-teal-400" :
                liveStatus === "connecting" ? "bg-amber-400" : "bg-zinc-500"
              }`} />
            </span>
            <span data-testid="ws-live-status">
              {liveStatus === "connected" ? "Live" : liveStatus === "connecting" ? "Connecting" : "Polling"}
            </span>
            {" · "}
            {lastRefresh ? fmtRelativeTime(lastRefresh.toISOString()) : "—"}
          </span>
          <button onClick={fetchDevices} title="Refresh"
            className="p-1.5 rounded-sm text-zinc-500 hover:text-teal-400 hover:bg-zinc-800 transition-colors"
            data-testid="map-refresh-btn">
            <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Body: Left panel + Map */}
      <div className="flex flex-1 min-h-0">
        {/* Left Device Panel */}
        <aside className="w-[340px] shrink-0 flex flex-col border-r border-zinc-800 bg-zinc-950/30"
          data-testid="map-device-panel">
          {/* Search */}
          <div className="p-3 border-b border-zinc-800">
            <div className="relative">
              <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, ID, location, customer..."
                className="pl-7 h-8 text-xs bg-zinc-900 border-zinc-800"
                data-testid="map-panel-search" />
            </div>
          </div>

          {/* Filters */}
          <div className="p-2 border-b border-zinc-800 flex flex-wrap gap-1">
            {FILTERS.map((f) => {
              const active = filter === f.key;
              const cnt = f.key === "all"
                ? counts.total
                : ["critical", "warning", "normal", "offline"].includes(f.key)
                  ? counts[f.key]
                  : classifiedDevices.filter((d) => d._flags[f.key]).length;
              return (
                <button key={f.key} onClick={() => setFilter(f.key)}
                  data-testid={`map-filter-${f.key}`}
                  className={`text-[10px] font-mono px-2 py-1 rounded-sm border transition-colors ${
                    active
                      ? "bg-teal-500/20 text-teal-400 border-teal-700"
                      : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"
                  }`}>
                  {f.label}
                  <span className="ml-1 opacity-60">{cnt}</span>
                </button>
              );
            })}
          </div>

          {/* Device list */}
          <div className="flex-1 overflow-y-auto" data-testid="map-device-list">
            {loading ? (
              <div className="text-xs font-mono text-zinc-500 animate-pulse text-center py-6">Loading devices...</div>
            ) : filteredDevices.length === 0 ? (
              <div className="text-xs text-zinc-600 text-center py-6">No devices match</div>
            ) : (
              filteredDevices.map((d) => (
                <DeviceRow key={d.device_name}
                  device={d}
                  selected={selectedDevice === d.device_name}
                  onClick={() => focusDevice(d)}
                />
              ))
            )}
          </div>
        </aside>

        {/* Map */}
        <div className="flex-1 relative min-w-0">
          <div ref={mapRef} className="absolute inset-0" style={{ background: "#1A1A1A" }}
            data-testid="fleet-map-container" />

          {/* Geofence toolbar: address search + Draw Geofence */}
          <div className="absolute top-3 left-3 z-[1000] flex flex-col gap-2 w-80"
            data-testid="geofence-toolbar">
            <div className="relative">
              <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input value={addressQuery} onChange={(e) => setAddressQuery(e.target.value)}
                onFocus={() => setAddressFocused(true)}
                onBlur={() => setTimeout(() => setAddressFocused(false), 150)}
                placeholder="Search address or place — try 'Memphis, TN' or '350 5th Ave'"
                className="pl-7 pr-7 h-8 text-xs bg-zinc-950/95 border-zinc-800 backdrop-blur-md text-zinc-200"
                data-testid="map-address-search" />
              {addressQuery && (
                <button onClick={() => { setAddressQuery(""); setAddressResults([]); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200"
                  data-testid="map-address-clear">
                  <X size={12} />
                </button>
              )}
              {addressFocused && (addressResults.length > 0 || addressLoading) && (
                <div className="absolute top-full mt-1 left-0 right-0 bg-zinc-950/98 border border-zinc-800 backdrop-blur-md rounded-sm overflow-hidden shadow-2xl max-h-80 overflow-y-auto"
                  data-testid="map-address-results">
                  {addressLoading && (
                    <div className="px-3 py-2 text-[10px] text-zinc-500 font-mono">Searching…</div>
                  )}
                  {addressResults.map((r) => (
                    <button key={r.place_id}
                      // Use onMouseDown + preventDefault to win the race against
                      // the input's onBlur (which would otherwise unmount this
                      // dropdown before the click handler can fire).
                      onMouseDown={(e) => { e.preventDefault(); flyToAddress(r); }}
                      data-testid={`address-result-${r.place_id}`}
                      className="w-full text-left px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-900 border-b border-zinc-900 last:border-b-0">
                      <div className="font-medium text-zinc-200 truncate">{r.name || r.display_name.split(",")[0]}</div>
                      <div className="text-[10px] text-zinc-500 truncate">{r.display_name}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {drawMode === "idle" && !showDraftForm && (
                <Button size="sm" variant="outline" onClick={startDraw}
                  className="border-zinc-700 hover:bg-zinc-800 text-xs h-8"
                  title="Advanced: draw a raw geofence without a Facility wrapper. For most cases, create a Facility under Admin Tools → Parties."
                  data-testid="map-draw-geofence-btn">
                  <MapPinArea size={12} className="mr-1.5" />
                  Draw Custom Geofence
                </Button>
              )}
              {drawMode === "idle" && !showDraftForm && (
                <div className="inline-flex items-center bg-zinc-950/90 backdrop-blur-md border border-zinc-800 rounded-sm overflow-hidden"
                  data-testid="basemap-toggle">
                  <button onClick={() => setBasemap("dark")}
                    data-testid="basemap-dark-btn"
                    className={`text-[10px] font-mono px-2.5 h-8 transition-colors ${
                      basemap === "dark"
                        ? "bg-teal-500/15 text-teal-300"
                        : "text-zinc-500 hover:text-zinc-200 hover:bg-zinc-900"
                    }`}>
                    Dark
                  </button>
                  <button onClick={() => setBasemap("satellite")}
                    data-testid="basemap-satellite-btn"
                    className={`text-[10px] font-mono px-2.5 h-8 transition-colors border-l border-zinc-800 ${
                      basemap === "satellite"
                        ? "bg-teal-500/15 text-teal-300"
                        : "text-zinc-500 hover:text-zinc-200 hover:bg-zinc-900"
                    }`}>
                    Satellite
                  </button>
                </div>
              )}
              {drawMode !== "idle" && (
                <>
                  <div className="bg-teal-500/10 text-teal-300 border border-teal-500/30 text-[10px] font-mono px-2.5 py-1.5 rounded-sm flex items-center gap-1.5"
                    data-testid="draw-mode-hint">
                    <Crosshair size={11} weight="bold" />
                    {drawMode === "center" ? "Click map to drop center" : "Click map to set radius"}
                  </div>
                  <Button size="sm" variant="outline" onClick={cancelDraw}
                    className="border-zinc-700 hover:bg-zinc-800 text-xs h-7"
                    data-testid="map-cancel-draw-btn">
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Draft form — appears after the second click sets the radius */}
          {showDraftForm && draft && (
            <div className="absolute bottom-4 left-3 z-[1001] w-[360px] bg-zinc-950/98 border border-teal-500/30 backdrop-blur-md p-4 rounded-sm shadow-2xl"
              data-testid="geofence-draft-form">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MapPinArea size={16} weight="duotone" className="text-teal-400" />
                  <span className="text-sm font-semibold text-zinc-100">New Geofence</span>
                </div>
                <button onClick={cancelDraw} className="text-zinc-500 hover:text-zinc-200"
                  data-testid="draft-form-close"><X size={14} /></button>
              </div>
              <div className="space-y-2.5">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Name *</label>
                  <Input value={draftName} onChange={(e) => setDraftName(e.target.value)}
                    placeholder="Memphis Distribution Center"
                    autoFocus
                    className="bg-zinc-900 border-zinc-800 text-zinc-200 h-8 text-sm"
                    data-testid="draft-form-name" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Type</label>
                  <select value={draftType} onChange={(e) => setDraftType(e.target.value)}
                    className="bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm rounded-md px-3 py-1.5 w-full h-8"
                    data-testid="draft-form-type">
                    {["Warehouse","Distribution Center","Airport Cargo Facility","Port","Customer Facility","Hospital","Pharmacy","Origin","Destination","Custom"].map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-3 gap-2 text-[10px] font-mono text-zinc-500 bg-zinc-900/60 border border-zinc-800 rounded-sm px-2 py-1.5"
                  data-testid="draft-form-coords">
                  <div><div className="text-zinc-600 uppercase">Lat</div><div className="text-zinc-300">{draft.lat.toFixed(5)}</div></div>
                  <div><div className="text-zinc-600 uppercase">Lng</div><div className="text-zinc-300">{draft.lng.toFixed(5)}</div></div>
                  <div><div className="text-zinc-600 uppercase">Radius</div><div className="text-teal-300">{draft.radius_m >= 1000 ? `${(draft.radius_m/1000).toFixed(draft.radius_m % 1000 === 0 ? 0 : 2)} km` : `${draft.radius_m} m`}</div></div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Notes</label>
                  <Textarea value={draftNotes} onChange={(e) => setDraftNotes(e.target.value)}
                    rows={2} placeholder="Operating hours, dock #, contact…"
                    className="bg-zinc-900 border-zinc-800 text-zinc-200 text-xs"
                    data-testid="draft-form-notes" />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => { setShowDraftForm(false); setDrawMode("radius"); }}
                    className="border-zinc-700 hover:bg-zinc-800 text-xs flex-1"
                    data-testid="draft-form-redraw-radius">
                    Redraw Radius
                  </Button>
                  <Button size="sm" onClick={saveDraft}
                    className="bg-teal-500 hover:bg-teal-400 text-zinc-950 text-xs flex-1"
                    data-testid="draft-form-save">
                    <CheckCircle size={12} weight="bold" className="mr-1" />
                    Save Geofence
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Geofences panel — opens on demand from the status-bar button */}
          {geofencePanelOpen && (
            <GeofenceBrowserPanel
              geofences={geofences}
              showGeofences={showGeofences}
              setShowGeofences={setShowGeofences}
              hiddenTypes={hiddenTypes}
              setHiddenTypes={setHiddenTypes}
              search={geofenceSearch}
              setSearch={setGeofenceSearch}
              onFlyTo={flyToGeofence}
              onFlyToAll={flyToAllGeofences}
              onClose={() => setGeofencePanelOpen(false)}
            />
          )}

          {/* Edit-on-map floating panel (existing geofence) */}          {editing && (
            <div className="absolute bottom-4 left-3 z-[1001] w-[360px] bg-zinc-950/98 border border-teal-500/30 backdrop-blur-md p-4 rounded-sm shadow-2xl"
              data-testid="geofence-edit-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MapPinArea size={16} weight="duotone" className="text-teal-400" />
                  <span className="text-sm font-semibold text-zinc-100">Editing Geofence</span>
                  <span className="text-[10px] uppercase tracking-wider bg-teal-500/10 text-teal-300 border border-teal-500/30 px-1.5 py-0.5 rounded-sm">
                    Unsaved
                  </span>
                </div>
                <button onClick={cancelEdit} className="text-zinc-500 hover:text-zinc-200"
                  data-testid="edit-panel-close"><X size={14} /></button>
              </div>
              <div className="space-y-2.5">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Name *</label>
                  <Input value={editing.name || ""} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))}
                    className="bg-zinc-900 border-zinc-800 text-zinc-200 h-8 text-sm"
                    data-testid="edit-panel-name" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Type</label>
                  <select value={editing.type} onChange={(e) => setEditing((p) => ({ ...p, type: e.target.value }))}
                    className="bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm rounded-md px-3 py-1.5 w-full h-8"
                    data-testid="edit-panel-type">
                    {["Warehouse","Distribution Center","Airport Cargo Facility","Port","Customer Facility","Hospital","Pharmacy","Origin","Destination","Custom"].map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-3 gap-2 text-[10px] font-mono bg-zinc-900/60 border border-zinc-800 rounded-sm px-2 py-1.5"
                  data-testid="edit-panel-readout">
                  <div>
                    <div className="text-zinc-600 uppercase">Lat</div>
                    <div className={`${editing._orig?.latitude !== editing.latitude ? "text-teal-300" : "text-zinc-300"}`}>
                      {Number(editing.latitude).toFixed(5)}
                    </div>
                  </div>
                  <div>
                    <div className="text-zinc-600 uppercase">Lng</div>
                    <div className={`${editing._orig?.longitude !== editing.longitude ? "text-teal-300" : "text-zinc-300"}`}>
                      {Number(editing.longitude).toFixed(5)}
                    </div>
                  </div>
                  <div>
                    <div className="text-zinc-600 uppercase">Radius</div>
                    <div className={`${editing._orig?.radius_m !== editing.radius_m ? "text-teal-300 font-bold" : "text-zinc-300"}`}>
                      {editing.radius_m >= 1000
                        ? `${(editing.radius_m / 1000).toFixed(editing.radius_m % 1000 === 0 ? 0 : 2)} km`
                        : `${Math.round(editing.radius_m)} m`}
                    </div>
                  </div>
                </div>
                <div className="text-[10px] text-zinc-500 italic">
                  Drag the teal center to move · drag the edge handle to resize · live values shown above.
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Notes</label>
                  <Textarea value={editing.notes || ""} onChange={(e) => setEditing((p) => ({ ...p, notes: e.target.value }))}
                    rows={2}
                    className="bg-zinc-900 border-zinc-800 text-zinc-200 text-xs"
                    data-testid="edit-panel-notes" />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={cancelEdit} disabled={savingEdit}
                    className="border-zinc-700 hover:bg-zinc-800 text-xs flex-1"
                    data-testid="edit-panel-cancel">
                    Cancel
                  </Button>
                  <Button size="sm" onClick={saveEdit} disabled={savingEdit}
                    className="bg-teal-500 hover:bg-teal-400 text-zinc-950 text-xs flex-1"
                    data-testid="edit-panel-save">
                    <CheckCircle size={12} weight="bold" className="mr-1" />
                    {savingEdit ? "Saving…" : "Save Changes"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Floating popup */}
          {popup && (
            <div className="absolute top-3 right-3 w-[280px] bg-zinc-900/95 border border-zinc-800 backdrop-blur-md p-4 rounded-sm shadow-2xl z-[1000]"
              data-testid="map-device-popup">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: SEV_COLORS[popup._flags.severity] }} />
                  <span className="text-sm font-mono font-semibold text-zinc-100">{popup.device_name}</span>
                </div>
                <button onClick={() => setPopup(null)} className="text-zinc-500 hover:text-zinc-300 text-xs leading-none -mt-1">×</button>
              </div>
              {/* Shipment association — clickable when present, otherwise a clear muted note */}
              {popup.shipment_id ? (
                <button onClick={() => onSelectShipment?.(popup.shipment_id)}
                  className="w-full text-left mb-2 px-2 py-1.5 rounded-sm bg-teal-500/10 border border-teal-500/30 hover:bg-teal-500/15 hover:border-teal-500/50 transition-colors group"
                  data-testid="map-device-popup-shipment">
                  <div className="text-[9px] uppercase tracking-wider text-teal-300/70 font-mono">Shipment</div>
                  <div className="text-[11px] font-mono text-teal-200 truncate group-hover:text-teal-100">
                    {popup.shipment_name || popup.shipment_id.slice(0, 8)}
                  </div>
                  {popup.customer && (
                    <div className="text-[9px] font-mono text-zinc-500 truncate">{popup.customer}</div>
                  )}
                </button>
              ) : (
                <div className="mb-2 px-2 py-1 rounded-sm border border-zinc-800/60 text-[9px] font-mono text-zinc-600 italic"
                  data-testid="map-device-popup-no-shipment">
                  Not assigned to a shipment
                </div>
              )}
              <p className="text-[10px] text-zinc-500 mb-3 line-clamp-2">{popup.address || "Unknown location"}</p>
              <div className="grid grid-cols-2 gap-2 text-[11px] font-mono mb-3">
                <Stat icon={Thermometer} label="Temp" value={popup.temperature != null ? `${popup.temperature}°F` : "—"} color="text-teal-400" />
                <Stat icon={Drop} label="Hum" value={popup.humidity != null ? `${popup.humidity}%` : "—"} color="text-emerald-400" />
                <Stat icon={Lightbulb} label="Light" value={popup.light || "—"}
                  color={popup._flags.light_breach ? "text-amber-400" : "text-zinc-500"} />
                <Stat icon={BatteryFull} label="Bat" value={popup.battery_level != null ? `${popup.battery_level}%` : "—"}
                  color={popup.battery_level < 20 ? "text-red-400" : "text-zinc-300"} />
              </div>
              <p className="text-[9px] font-mono text-zinc-600 mb-3">Last: {fmtRelativeTime(popup.last_reading)}</p>
              <Button size="sm" onClick={() => onSelectDevice(popup.device_name, popup.dataset_id)}
                className="w-full bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-8 font-mono"
                data-testid="map-view-device-btn">
                View Detail <CaretRight size={12} className="ml-1" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GeofenceBrowserPanel({
  geofences, showGeofences, setShowGeofences,
  hiddenTypes, setHiddenTypes, search, setSearch,
  onFlyTo, onFlyToAll, onClose,
}) {
  const allTypes = Array.from(new Set(geofences.map((g) => g.type))).sort();
  const toggleType = (t) => setHiddenTypes((prev) => {
    const next = new Set(prev);
    if (next.has(t)) next.delete(t); else next.add(t);
    return next;
  });

  const filtered = geofences.filter((g) => {
    if (hiddenTypes.has(g.type)) return false;
    if (search) return (g.name + " " + g.type).toLowerCase().includes(search.toLowerCase());
    return true;
  });

  const TYPE_COLOR = {
    "Warehouse": "#2dd4bf", "Distribution Center": "#06b6d4",
    "Airport Cargo Facility": "#a78bfa", "Port": "#60a5fa",
    "Customer Facility": "#34d399", "Hospital": "#f87171",
    "Pharmacy": "#fb923c", "Origin": "#facc15",
    "Destination": "#84cc16", "Custom": "#9ca3af",
  };
  const fmt = (m) =>
    m >= 1000 ? `${(m/1000).toFixed(m % 1000 === 0 ? 0 : 2)} km` : `${Math.round(m)} m`;

  return (
    <div className="absolute top-3 right-3 z-[1001] w-[360px] bg-zinc-950/98 border border-zinc-800 backdrop-blur-md rounded-sm shadow-2xl flex flex-col"
      style={{ maxHeight: "calc(100% - 24px)" }}
      data-testid="geofence-browser-panel">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPinArea size={15} weight="duotone" className="text-teal-400" />
          <span className="text-sm font-semibold text-zinc-100">Geofences</span>
          <span className="text-[10px] font-mono text-zinc-500">({geofences.length})</span>
        </div>
        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"
          data-testid="geofence-panel-close"><X size={15} /></button>
      </div>

      {/* Controls */}
      <div className="p-3 border-b border-zinc-800 space-y-2.5">
        <div className="relative">
          <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or type"
            className="pl-7 h-8 text-xs bg-zinc-900 border-zinc-800 text-zinc-200"
            data-testid="geofence-panel-search" />
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={onFlyToAll}
            disabled={filtered.length === 0}
            className="bg-teal-500 hover:bg-teal-400 text-zinc-950 text-xs h-7 flex-1"
            data-testid="geofence-panel-show-all">
            <Target size={11} weight="bold" className="mr-1.5" /> Show All on Map
          </Button>
          <button onClick={() => setShowGeofences((v) => !v)}
            className={`text-[10px] font-mono px-2.5 h-7 rounded-sm border transition-colors ${
              showGeofences
                ? "bg-teal-500/10 text-teal-300 border-teal-500/30"
                : "bg-zinc-900 text-zinc-500 border-zinc-800"
            }`}
            data-testid="geofence-panel-visibility">
            Layer {showGeofences ? "ON" : "OFF"}
          </button>
        </div>
        {/* Type chips */}
        {allTypes.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {allTypes.map((t) => {
              const hidden = hiddenTypes.has(t);
              const color = TYPE_COLOR[t] || "#9ca3af";
              return (
                <button key={t} onClick={() => toggleType(t)}
                  data-testid={`geofence-panel-type-${t.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`text-[10px] px-2 py-1 rounded-sm border transition-colors flex items-center gap-1 ${
                    hidden ? "bg-zinc-900 text-zinc-600 border-zinc-800 line-through" : "bg-zinc-900 text-zinc-300 border-zinc-700"
                  }`}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: hidden ? "#3f3f46" : color }} />
                  {t}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-xs text-zinc-500 italic">
            {geofences.length === 0
              ? "No geofences in this org yet. Use the Draw Geofence button to create one."
              : "No matches. Adjust search or type filters."}
          </div>
        ) : filtered.map((g) => (
          <button key={g.id}
            onClick={() => onFlyTo(g)}
            data-testid={`geofence-panel-row-${g.id}`}
            className="w-full text-left px-3 py-2.5 border-b border-zinc-900 hover:bg-zinc-900/60 transition-colors flex items-center gap-3 group">
            <span className="w-2 h-2 rounded-full shrink-0"
              style={{ background: TYPE_COLOR[g.type] || "#9ca3af" }} />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-zinc-200 truncate">{g.name}</div>
              <div className="text-[10px] text-zinc-500 font-mono">
                {g.type} · {fmt(g.radius_m)}
              </div>
            </div>
            <Target size={12} className="text-zinc-600 group-hover:text-teal-400 transition-colors shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

// Removed: GeofenceTypeFilter (replaced by inline chips inside GeofenceBrowserPanel)
function StatusPill({ label, count, color, testid }) {  const map = {
    red:     { bg: "bg-red-500/15", text: "text-red-400", dot: "bg-red-400" },
    amber:   { bg: "bg-amber-500/15", text: "text-amber-400", dot: "bg-amber-400" },
    zinc:    { bg: "bg-zinc-500/15", text: "text-zinc-400", dot: "bg-zinc-500" },
    emerald: { bg: "bg-emerald-500/15", text: "text-emerald-400", dot: "bg-emerald-400" },
  };
  const c = map[color];
  return (
    <span className={`flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-mono ${c.bg} ${c.text}`}
      data-testid={testid}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {label} <span className="font-bold">{count}</span>
    </span>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center gap-1">
      <Icon size={11} className={color} />
      <span className={`${color} truncate`}>{value}</span>
    </div>
  );
}

function DeviceRow({ device, selected, onClick }) {
  const sev = device._flags.severity;
  const sevColor = SEV_COLORS[sev];
  return (
    <button
      onClick={onClick}
      data-testid={`map-row-${device.device_name}`}
      className={`w-full text-left px-3 py-2.5 border-b border-zinc-900 transition-colors ${
        selected
          ? "bg-teal-500/10 border-l-2 border-l-teal-400"
          : "hover:bg-zinc-900 border-l-2 border-l-transparent"
      }`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: sevColor }} />
          <span className="text-xs font-mono font-semibold text-zinc-100 truncate">{device.device_name}</span>
        </div>
        <span className="text-[9px] font-mono uppercase tracking-wider opacity-70 shrink-0"
          style={{ color: sevColor }}>{sev}</span>
      </div>
      <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-500 ml-3.5">
        {device.temperature != null && (
          <span className={device._flags.temp_excursion ? "text-amber-400" : ""}>
            {device.temperature}°F
          </span>
        )}
        {device.humidity != null && (
          <span className={device._flags.humidity_excursion ? "text-amber-400" : ""}>
            {device.humidity}%
          </span>
        )}
        {device._flags.light_breach && (
          <span className="text-amber-400 flex items-center gap-0.5"><Lightbulb size={9} />light</span>
        )}
      </div>
      {device.shipment_id && (
        <div className="ml-3.5 mt-1 flex items-center gap-1 text-[9px] font-mono text-teal-300 truncate"
          title={device.shipment_name || device.shipment_id}>
          <span className="w-1 h-1 rounded-full bg-teal-400 flex-shrink-0" />
          <span className="truncate">{device.shipment_name || device.shipment_id.slice(0, 8)}</span>
        </div>
      )}
      <div className="flex items-center justify-between mt-1 ml-3.5">
        <span className="text-[9px] font-mono text-zinc-600 truncate flex items-center gap-1">
          {sev === "offline" && <WifiSlash size={9} className="text-zinc-500" />}
          {fmtRelativeTime(device.last_reading)}
        </span>
        {device.alert_count > 0 && (
          <span className="text-[9px] font-mono text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded-sm">
            {device.alert_count} alert{device.alert_count > 1 ? "s" : ""}
          </span>
        )}
      </div>
    </button>
  );
}
